import React, { useEffect } from 'react';
import ProcessingModal from '@/components/dashboard-modal/ProcessingModal';
import { useProjectCreation } from '@/hooks/content-generator/use-project-creation';
import { ProjectGenerationRequest } from '@/types/project-generation-types';

interface ProjectGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestData: ProjectGenerationRequest | null;
  onSuccess: (projectId: string, goalId: string) => void;
}

const ProjectGenerationModal: React.FC<ProjectGenerationModalProps> = ({
  isOpen,
  onClose,
  requestData,
  onSuccess,
}) => {
  const { state, startCreation, reset } = useProjectCreation();

  useEffect(() => {
    if (isOpen && requestData && state.status === 'idle') {
      startCreation(requestData);
    }
  }, [isOpen, requestData, startCreation, state.status]);

  const handleClose = () => {
    reset();
    onClose();
  };
  
  const handleRetry = () => {
    if (requestData) {
      startCreation(requestData);
    }
  };
  
  const handleSuccess = () => {
    if (state.generatedProjectId && state.generatedGoalId) {
      onSuccess(state.generatedProjectId, state.generatedGoalId);
    }
    handleClose();
  };

  // Calculate progress message based on the user's goal
  const getProgressMessage = () => {
    const userGoal = requestData?.userGoal || 'your project';
    return `Your new project has been created with AI-generated tasks and research recommendations!`;
  };

  return (
    <ProcessingModal
      isOpen={isOpen}
      onClose={handleClose}
      state={state.status === 'processing' ? 'processing' : state.status === 'success' ? 'success' : 'error'}
      tasks={state.tasks}
      progress={state.progress}
      currentTaskDescription={state.currentTaskDescription}
      errorMessage={state.error || ''}
      onRetry={handleRetry}
      successMessage={getProgressMessage()}
      successActionText="Continue"
      onSuccessAction={handleSuccess}
      title="✨ Creating Your Project"
      description="Our AI is designing your project structure and workspace based on your vision."
    />
  );
};

export default ProjectGenerationModal; 